/* ============================================================
   BT FX — bold interaction layer  (bt-fx.js)
   ------------------------------------------------------------
   Additive, defer-loaded via snippets/meta-tags.liquid. Every
   feature is null-guarded + try/wrapped so it can NEVER break the
   storefront. Performance rules kept throughout:
     • only transform/opacity are animated (GPU compositor)
     • ONE passive scroll listener, rAF-throttled
     • ONE passive pointer loop for the cursor
     • IntersectionObserver for reveals (zero scroll cost)
     • pointer effects skipped on touch / reduced-motion
   Safe to remove: delete the bt-fx.js line in meta-tags.liquid.
   ============================================================ */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine   = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  }
  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  /* ---------- 1. Scroll progress + condensed header ---------- */
  function initScroll() {
    try {
      var bar = document.createElement('div');
      bar.className = 'bt-progress';
      document.body.appendChild(bar);

      var ticking = false;
      function update() {
        var doc = document.documentElement;
        var max = (doc.scrollHeight - doc.clientHeight) || 1;
        var pct = clamp(window.scrollY / max, 0, 1);
        bar.style.transform = 'scaleX(' + pct + ')';
        document.body.classList.toggle('bt-scrolled', window.scrollY > 40);
        ticking = false;
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
    } catch (e) {}
  }

  /* ---------- 2. Trailing ember cursor ---------- */
  function initCursor() {
    if (!fine || reduce) return;
    try {
      var ring = document.createElement('div');  ring.className = 'bt-cursor';
      var dot  = document.createElement('div');  dot.className  = 'bt-cursor-dot';
      document.body.appendChild(ring);
      document.body.appendChild(dot);

      var mx = window.innerWidth / 2, my = window.innerHeight / 2;   // target
      var rx = mx, ry = my;                                          // eased ring
      var active = false;

      document.addEventListener('mousemove', function (e) {
        mx = e.clientX; my = e.clientY;
        dot.style.transform = 'translate3d(' + mx + 'px,' + my + 'px,0)';
        if (!active) {
          active = true;
          ring.classList.add('is-active');
          dot.classList.add('is-active');
        }
        // warm/swell the ring over interactive elements
        var hot = e.target && e.target.closest &&
          e.target.closest('a, button, .button, input, select, textarea, [role="button"], .card-wrapper, summary, label');
        ring.classList.toggle('is-hot', !!hot);
      }, { passive: true });

      document.addEventListener('mouseleave', function () {
        active = false;
        ring.classList.remove('is-active');
        dot.classList.remove('is-active');
      });

      (function loop() {
        rx += (mx - rx) * 0.18;
        ry += (my - ry) * 0.18;
        ring.style.transform = 'translate3d(' + rx + 'px,' + ry + 'px,0)';
        requestAnimationFrame(loop);
      })();
    } catch (e) {}
  }

  /* ---------- 3. Magnetic buttons ---------- */
  function initMagnetic() {
    if (!fine || reduce) return;
    try {
      var sel = '.button:not(.button--secondary):not(.button--tertiary), .bt-shop-btn, .product-form__submit, .quick-add__submit';
      var btns = document.querySelectorAll(sel);
      Array.prototype.forEach.call(btns, function (btn) {
        if (btn.closest('.bt-buybar')) return;      // leave the sticky bar alone
        btn.classList.add('bt-magnetic');
        var strength = 0.35, cap = 10;
        btn.addEventListener('mousemove', function (e) {
          var r = btn.getBoundingClientRect();
          var dx = clamp((e.clientX - (r.left + r.width / 2)) * strength, -cap, cap);
          var dy = clamp((e.clientY - (r.top + r.height / 2)) * strength, -cap, cap);
          btn.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        });
        btn.addEventListener('mouseleave', function () { btn.style.transform = ''; });
      });
    } catch (e) {}
  }

  /* ---------- 4. Click ember burst ---------- */
  function initBurst() {
    if (reduce) return;
    try {
      document.addEventListener('click', function (e) {
        var t = e.target && e.target.closest && e.target.closest('.button, .bt-shop-btn, .product-form__submit, .quick-add__submit, .bt-buybar__btn');
        if (!t) return;
        var burst = document.createElement('div');
        burst.className = 'bt-burst';
        burst.style.left = e.clientX + 'px';
        burst.style.top  = e.clientY + 'px';
        var n = 10;
        for (var i = 0; i < n; i++) {
          var s = document.createElement('span');
          s.className = 'bt-spark';
          var ang = (Math.PI * 2 * i) / n + Math.random() * 0.5;
          var dist = 26 + Math.random() * 26;
          s.style.setProperty('--dx', Math.cos(ang) * dist + 'px');
          s.style.setProperty('--dy', Math.sin(ang) * dist + 'px');
          burst.appendChild(s);
        }
        document.body.appendChild(burst);
        setTimeout(function () { burst.remove(); }, 650);
      }, { passive: true });
    } catch (e) {}
  }

  /* ---------- 5. Hero spotlight + embers ---------- */
  function initHero() {
    try {
      var hero = document.querySelector('#MainContent > .shopify-section:first-child');
      if (!hero) return;
      hero.classList.add('bt-hero-fx');

      var glow = document.createElement('div');
      glow.className = 'bt-hero-glow';
      hero.insertBefore(glow, hero.firstChild);

      // rising embers (skipped under reduced motion via CSS display:none)
      if (!reduce) {
        for (var i = 0; i < 7; i++) {
          var em = document.createElement('span');
          em.className = 'bt-ember';
          em.style.left = (6 + Math.random() * 88) + '%';
          em.style.animationDuration = (5 + Math.random() * 4) + 's';
          em.style.animationDelay = (Math.random() * 5) + 's';
          var sz = 4 + Math.random() * 5;
          em.style.width = sz + 'px';
          em.style.height = sz + 'px';
          glow.appendChild(em);
        }
      }

      // cursor spotlight
      if (fine && !reduce) {
        var pending = false, lastX = 50, lastY = 50;
        hero.addEventListener('mousemove', function (e) {
          var r = hero.getBoundingClientRect();
          lastX = ((e.clientX - r.left) / r.width) * 100;
          lastY = ((e.clientY - r.top) / r.height) * 100;
          if (!pending) {
            pending = true;
            requestAnimationFrame(function () {
              hero.style.setProperty('--mx', lastX + '%');
              hero.style.setProperty('--my', lastY + '%');
              pending = false;
            });
          }
        }, { passive: true });
        hero.addEventListener('mouseenter', function () { hero.classList.add('is-live'); });
        hero.addEventListener('mouseleave', function () { hero.classList.remove('is-live'); });
      }
    } catch (e) {}
  }

  /* ---------- 6. Section reveal on scroll ---------- */
  function initReveal() {
    if (reduce || !('IntersectionObserver' in window)) return;
    try {
      var sections = document.querySelectorAll('#MainContent > .shopify-section');
      var targets = [];
      Array.prototype.forEach.call(sections, function (sec, idx) {
        if (idx === 0) return;                          // never hide the hero
        if (sec.querySelector('.scroll-trigger')) return; // don't fight Dawn's own reveal
        sec.classList.add('bt-reveal');
        targets.push(sec);
      });
      if (!targets.length) return;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('bt-in');
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      targets.forEach(function (t) { io.observe(t); });

      // failsafe: never leave content hidden
      setTimeout(function () {
        targets.forEach(function (t) { t.classList.add('bt-in'); });
      }, 2600);
    } catch (e) {}
  }

  /* ---------- 7. Blog / article card tilt ---------- */
  function initTilt() {
    try {
      var tmpl = (document.querySelector('main[data-template]') || {}).dataset;
      var isBloggy = tmpl && /blog|article|index/.test(tmpl.template || '');
      var cards = document.querySelectorAll('.article-card, .blog-articles .card-wrapper, .featured-blog .card-wrapper');
      if (!cards.length) return;

      Array.prototype.forEach.call(cards, function (card) {
        card.classList.add('bt-tilt');                 // enables CSS image zoom everywhere
        if (!fine || reduce) return;                   // 3D tilt only on fine pointer
        var max = 7;
        card.addEventListener('mousemove', function (e) {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          card.style.transform =
            'perspective(700px) rotateY(' + (px * max) + 'deg) rotateX(' + (-py * max) + 'deg) translateY(-4px)';
        });
        card.addEventListener('mouseleave', function () { card.style.transform = ''; });
      });
    } catch (e) {}
  }

  ready(function () {
    initScroll();
    initCursor();
    initMagnetic();
    initBurst();
    initHero();
    initReveal();
    initTilt();
  });
})();
